// Service intake: optional native Shopify text request; no file upload, persistence or orders.
// Live prices must be calculated/validated by an authorized backend, not trusted
// from browser fields. These details never certify cylinder/equipment suitability.
const choices = values => values.map(value => [value, value]);
const field = (key, label, options = {}) => ({ key, label, ...options });
const makeModel = [
  field('make', 'Manufacturer / brand', { required: true, hint: 'If unknown, enter “Unknown” and add a photo on the next step.' }),
  field('model', 'Model', { required: true }),
];
const cylinderFields = [
  field('capacity', 'Cylinder capacity', { type: 'number', min: .1, max: 10000, step: 'any', hint: 'Copy the known capacity. Leave blank if unsure.' }),
  field('capacity_unit', 'Capacity units', { type: 'select', options: [['', 'Select units'], ['cu ft (rated gas capacity)', 'cu ft (gas)'], ['L (water volume)', 'L (water)']] }),
  field('material', 'Cylinder material', { type: 'select', options: choices(['Unknown', 'Aluminium', 'Steel', 'Composite', 'Other']) }),
  field('pressure', 'Stamped service pressure', { type: 'number', min: 1, max: 10000, step: 'any', hint: 'The working/service pressure, not the hydrostatic test pressure. Leave blank if unsure.' }),
  field('pressure_unit', 'Pressure units', { type: 'select', options: [['', 'Select units'], ...choices(['psi', 'bar'])] }),
  field('quantity', 'Number of matching cylinders', { type: 'number', min: 1, max: 20, step: 1, value: '1', required: true, hint: 'Same size, material and requested work. Different specifications need separate requests.' }),
];
const cylinderDetails = [
  field('cylinder_make', 'Cylinder manufacturer / model', { hint: 'Optional—copy what you can read.' }),
  field('markings', 'Cylinder stampings / markings', { type: 'textarea', hint: 'Copy the markings as written, or add a clear photo. You do not need to interpret them.' }),
  field('test_date', 'Latest hydrostatic test marking', { hint: 'Optional. Enter the marked date as written; leave blank if unknown.' }),
  field('inspection', 'Visual-inspection sticker / date', { hint: 'Optional. Enter what the sticker says, if present.' }),
  field('valve', 'Valve connection', { type: 'select', options: choices(['Unknown', 'DIN', 'Yoke', 'Other']) }),
  field('condition', 'Condition or known concerns', { type: 'textarea', hint: 'Optional. Mention damage, corrosion, leaks or unusual history. Do not dismantle or test the cylinder yourself.' }),
];
const definitions = {
  other: {
    first: [field('request', 'What would you like to discuss?', { type: 'textarea', required: true, hint: 'Describe your service question or other request, including equipment details if applicable.' })],
    second: [],
  },
  gas: {
    first: [field('gas', 'Requested gas', { type: 'select', required: true, options: [['', 'Choose gas'], ...choices(['Air', 'Nitrox', 'Trimix / mixed gas', 'Oxygen', 'Helium', 'Argon'])] }),
      field('oxygen', 'Requested oxygen (%)', { type: 'number', min: .1, max: 100, step: 'any', gases: ['Nitrox', 'Trimix / mixed gas'], hint: 'Optional—enter the mixture you are requesting.' }),
      field('helium', 'Requested helium (%)', { type: 'number', min: 0, max: 100, step: 'any', gases: ['Trimix / mixed gas'] }),
      ...cylinderFields],
    second: [...cylinderDetails,
      field('contents', 'Current contents / pressure', { hint: 'Optional—include units and gas if known. Do not guess the mixture.' }),
      field('cleaning', 'Oxygen-cleaning records, if known', { hint: 'Optional. Records support the shop’s review, not an automatic compatibility approval.' }),
      field('application', 'Intended application', { required: true, hint: 'For example: scuba, rebreather, drysuit inflation or paintball. Argon is not breathing gas; pure helium is not a breathing mixture.' })],
  },
  cylinder: {
    first: [field('cylinder_work', 'Requested cylinder work', { type: 'checkboxes', required: true, options: choices(['Hydrostatic testing', 'Inspection', 'Cleaning / tumbling', 'Valve service', 'Custom cylinder coating', 'Assess an issue']) }), field('coating', 'Requested coating', { type: 'select', required: true, options: [['', 'Choose a coating'], ...choices(['Anodizing', 'Powder coating', 'Thermal-spray zinc coating', 'Not sure yet'])] }), ...cylinderFields],
    second: [...cylinderDetails],
  },
  regulator: {
    first: [...makeModel,
      field('components', 'Equipment or components to include', { type: 'checkboxes', required: true, hint: 'Regulators supply breathing gas. A buoyancy control device (BCD) or wing holds gas for buoyancy control; its inflator adds gas and its valves release it. Select the complete unit or the parts needing attention.', options: choices(['Regulator first stage', 'Primary second stage', 'Alternate second stage (octopus)', 'BCD / wing (buoyancy compensator)', 'Low-pressure inflator', 'Dump / overpressure valve', 'Drysuit inlet / exhaust valve', 'Regulator / inflator hoses', 'Submersible pressure gauge (SPG)', 'Rebreather component / bailout valve']) }),
      field('equipment_work', 'Requested work', { type: 'select', required: true, options: [['', 'Choose work'], ...choices(['Routine service', 'Leak / performance issue', 'Repair / troubleshooting', 'Assess the system'])] })],
    second: [field('component_models', 'Additional component brands / models', { type: 'textarea', hint: 'List mixed-brand components or specific rebreather components, if applicable.' }),
      field('last_service', 'Last service', { hint: 'Optional. An approximate date or “Unknown” is fine.' }),
      field('symptoms', 'Symptoms or service history', { type: 'textarea', hint: 'Describe what you noticed. No troubleshooting or disassembly is needed to complete this form.' })],
  },
  computer: {
    first: [...makeModel, field('computer_work', 'Requested computer service', { type: 'select', required: true, options: [['', 'Choose work'], ...choices(['Battery service', 'Display / button issue', 'Water ingress / damage', 'Transmitter issue', 'Other assessment'])] })],
    second: [field('accessories', 'Transmitter or accessories included', { hint: 'Optional—add a model if known.' }), field('symptoms', 'Symptoms or service history', { type: 'textarea', hint: 'Include anything the shop should know about the issue. Model support and parts are confirmed during review.' })],
  },
};
const commonDetails = [
  field('handoff', 'How would you get the equipment to us?', { type: 'select', required: true, options: [['', 'Choose an option'], ...choices(['Drop off at the shop', 'Discuss shipping arrangements', 'Not sure yet'])] }),
  field('notes', 'Anything else?', { type: 'textarea', hint: 'Optional. Add timing needs or other equipment details. No contact, payment or medical details in this preview.' }),
];

class ServiceRequest extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.form = this.querySelector('[data-service-form]');
    this.service = this.querySelector('[data-service-select]');
    this.error = this.querySelector('[data-error]');
    this.contact = this.querySelector('[data-service-contact]');
    this.contactMode = this.hasAttribute('data-contact-mode');
    if (this.contact?.querySelector('[data-contact-result]')) {
      this.querySelector('[data-service-fallback]').hidden = true;
      return; // Preserve Shopify error values or success; do not restart the wizard.
    }
    this.step = 0;
    this.photoVersion = 0;
    this.photoURL = null;
    this.photoBusy = false;
    this.service.addEventListener('change', () => this.configure(), { signal });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.advance(); }, { signal });
    this.form.addEventListener('input', event => { event.target.removeAttribute('aria-invalid'); this.error.textContent = ''; }, { signal });
    this.querySelector('[data-back]').addEventListener('click', () => this.showStep(this.step - 1), { signal });
    this.querySelector('[data-start-over]').addEventListener('click', () => {
      this.form.reset(); this.configure(); this.showStep(0); this.service.focus();
    }, { signal });
    this.querySelector('[data-photo]')?.addEventListener('change', () => this.loadPhoto(), { signal });
    this.querySelector('[data-remove-photo]')?.addEventListener('click', () => this.clearPhoto(), { signal });
    const key = new URL(location.href).searchParams.get('service');
    if (key && definitions[key] && [...this.service.options].some(o => o.value === key)) this.service.value = key;
    this.configure();
    if (key && this.service.value !== key) this.error.textContent = 'Choose a service below; that service link was not recognized.';
    this.form.hidden = false;
    this.querySelector('[data-service-fallback]').hidden = true;
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.clearPhoto();
  }
  configure() {
    this.clearPhoto();
    this.error.textContent = '';
    this.active = definitions[this.service.value];
    const adjust = def => this.contactMode && def.hint ? { ...def, hint: def.hint.replace('and add a photo on the next step', 'and describe the equipment').replace(', or add a clear photo', '').replace('No contact, payment or medical details in this preview.', 'Do not include payment or medical details.') } : def;
    this.active = this.active ? { first: this.active.first.map(adjust), second: this.active.second.map(adjust) } : null;
    this.details = commonDetails.map(adjust);
    if (this.service.value === 'other') this.details = this.details.map(def => def.key === 'handoff' ? { ...def, label: 'Equipment handoff (if applicable)', required: false } : def);
    this.fields = this.active ? [...this.active.first, ...this.active.second, ...this.details] : [];
    this.querySelector('[data-summary]').replaceChildren();
    this.querySelector('[data-equipment-fields]').replaceChildren();
    this.querySelector('[data-detail-fields]').replaceChildren();
    if (this.active) {
      this.active.first.forEach(def => this.addField(def, '[data-equipment-fields]'));
      [...this.active.second, ...this.details].forEach(def => this.addField(def, '[data-detail-fields]'));
      if (this.service.value === 'gas' || this.service.value === 'cylinder') {
        const details = document.createElement('details'), heading = document.createElement('summary');
        details.className = 'oh-request-extra'; heading.textContent = 'Cylinder markings & history (optional)'; details.append(heading);
        const content = document.createElement('div'); details.append(content);
        this.querySelector('[data-detail-fields]').prepend(details);
        for (const key of [...cylinderDetails.map(def => def.key), 'contents', 'cleaning']) {
          const label = this.querySelector(`[data-field="${key}"]`); if (label) content.append(label);
        }
        for (const [amount, unit] of [['capacity', 'capacity_unit'], ['pressure', 'pressure_unit']]) {
          const first = this.querySelector(`[data-field="${amount}"]`), second = this.querySelector(`[data-field="${unit}"]`);
          const group = document.createElement('div'); group.className = 'oh-request-grid'; first.before(group); group.append(first, second);
          const help = first.querySelector('small'); if (help) { help.className = 'oh-request-pair-hint'; group.append(help); }
        }
      }
      if (this.service.value === 'cylinder') {
        this.form.querySelectorAll('[name="cylinder_work"]').forEach(input => input.addEventListener('change', () => this.updateCoating()));
        this.updateCoating();
      }
      if (this.service.value === 'gas') {
        this.hint = document.createElement('p'); this.hint.className = 'oh-request-hint';
        this.querySelector('[data-equipment-fields]').prepend(this.hint);
        this.control('gas').addEventListener('change', () => this.updateGas());
        this.updateGas();
      }
      if (this.service.value === 'gas' || this.service.value === 'cylinder') {
        const note = document.createElement('p'); note.className = 'oh-request-hint';
        note.textContent = 'The shop checks the cylinder, markings, test status and compatibility before any fill or work. This form does not determine whether a cylinder is safe to fill.';
        this.querySelector('[data-detail-fields]').prepend(note);
      }
    }
    this.showStep(0, false);
  }
  addField(def, container) {
    const target = this.querySelector(container);
    const id = `${this.service.id}-${def.key}`;
    if (def.type === 'checkboxes') {
      const group = document.createElement('fieldset'); group.className = 'oh-request-fieldset'; group.dataset.field = def.key;
      const legend = document.createElement('legend'); legend.textContent = def.label; group.append(legend);
      if (def.hint) { const hint = document.createElement('p'); hint.className = 'oh-request-component-hint'; hint.id = `${id}-help`; hint.textContent = def.hint; group.setAttribute('aria-describedby', hint.id); group.append(hint); }
      def.options.forEach(([value, title], index) => {
        const label = document.createElement('label'); label.className = 'oh-request-checkbox'; label.htmlFor = `${id}-${index}`;
        const input = document.createElement('input'); input.type = 'checkbox'; input.name = def.key; input.value = value; input.id = label.htmlFor;
        label.append(input, document.createTextNode(title)); group.append(label);
      }); target.append(group); return;
    }
    const label = document.createElement('label'); label.className = 'oh-request-field'; label.htmlFor = id; label.dataset.field = def.key;
    const title = document.createElement('span'); title.textContent = def.label; label.append(title);
    let input;
    if (def.type === 'select') {
      input = document.createElement('select');
      def.options.forEach(([value, title]) => { const option = document.createElement('option'); option.value = value; option.textContent = title; input.append(option); });
    } else if (def.type === 'textarea') { input = document.createElement('textarea'); input.rows = 3; input.maxLength = 1000;
    } else { input = document.createElement('input'); input.type = def.type ?? 'text'; if (input.type === 'text') input.maxLength = 160; }
    input.name = def.key; input.id = id; input.required = !!def.required;
    for (const attr of ['min', 'max', 'step', 'value']) if (def[attr] !== undefined) input[attr] = def[attr];
    label.append(input);
    if (def.hint) { const help = document.createElement('small'); help.id = `${id}-help`; help.textContent = def.hint; input.setAttribute('aria-describedby', help.id); label.append(help); }
    target.append(label);
  }
  control(key) { return this.form.querySelector(`[name="${key}"]`); }
  updateGas() {
    const gas = this.control('gas').value;
    this.fields.filter(def => def.gases).forEach(def => {
      const input = this.control(def.key), enabled = def.gases.includes(gas);
      input.closest('label').hidden = !enabled; input.disabled = !enabled;
      if (!enabled) input.value = '';
    });
    this.hint.textContent = gas === 'Argon' ? 'Argon is for non-breathing applications such as drysuit inflation. It is not breathing gas.' : gas === 'Helium' ? 'Pure helium is not a breathing mixture. Describe the intended application on the next step.' : 'Gas and cylinder compatibility are confirmed by the shop.';
  }
  updateCoating() {
    const input = this.control('coating');
    if (!input) return;
    const enabled = [...this.form.querySelectorAll('[name="cylinder_work"]:checked')].some(option => option.value === 'Custom cylinder coating');
    input.closest('label').hidden = !enabled; input.disabled = !enabled;
    if (!enabled) input.value = '';
  }
  fail(message, input) {
    this.error.textContent = message;
    if (input) { input.setAttribute('aria-invalid', 'true'); input.focus(); }
    return false;
  }
  validStep() {
    const panel = this.querySelector(`[data-step="${this.step}"]`);
    for (const input of panel.querySelectorAll('input:not([type=file]), select, textarea')) {
      if (input.disabled) continue;
      if (!input.checkValidity() || (input.required && !input.value.trim())) return this.fail(`${input.closest('label')?.querySelector('span')?.textContent ?? 'Service'}: ${input.validationMessage || 'Please enter a value.'}`, input);
    }
    if (this.step === 0) {
      for (const def of this.active?.first ?? []) {
        if (def.type === 'checkboxes' && def.required && !this.form.querySelector(`[name="${def.key}"]:checked`)) return this.fail(`Select at least one option for ${def.label.toLowerCase()}.`, this.control(def.key));
      }
      for (const [amount, unit] of [['capacity','capacity_unit'],['pressure','pressure_unit']]) {
        if (this.control(amount)?.value && !this.control(unit)?.value) return this.fail(`Choose units for ${amount === 'capacity' ? 'cylinder capacity' : 'service pressure'}.`, this.control(unit));
      }
      if (this.control('gas')?.value === 'Trimix / mixed gas') {
        const oxygen = this.control('oxygen'), helium = this.control('helium');
        if (oxygen.value && helium.value && Number(oxygen.value) + Number(helium.value) > 100) return this.fail('Requested gas percentages cannot add up to more than 100%.', helium);
      }
    }
    if (this.photoBusy) return this.fail('Please wait for the photo preview to finish.');
    return true;
  }
  advance() {
    if (this.step >= 2 || !this.validStep()) return;
    if (this.step === 1) this.makeSummary();
    this.showStep(this.step + 1);
  }
  showStep(step, focus = true) {
    this.step = Math.max(0, Math.min(2, step)); this.error.textContent = '';
    this.querySelectorAll('[data-step]').forEach(panel => { panel.hidden = Number(panel.dataset.step) !== this.step; });
    this.querySelectorAll('[data-progress]').forEach(item => { if (Number(item.dataset.progress) === this.step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current'); });
    if (this.contact) this.contact.hidden = this.step !== 2;
    this.querySelector('[data-back]').hidden = this.step === 0;
    this.querySelector('[data-next]').hidden = this.step === 2;
    this.querySelector('[data-next]').textContent = this.step === 1 ? 'Review service request →' : 'Continue →';
    this.querySelector('[data-start-over]').hidden = this.step !== 2;
    if (focus) this.querySelector(`[data-step="${this.step}"] h2`).focus();
  }
  makeSummary() {
    const summary = this.querySelector('[data-summary]'); summary.replaceChildren();
    const row = (label, value) => { const wrap = document.createElement('div'), dt = document.createElement('dt'), dd = document.createElement('dd'); dt.textContent = label; dd.textContent = value; wrap.append(dt, dd); summary.append(wrap); return dd; };
    row('Requested service', this.service.selectedOptions[0].dataset.title || this.service.selectedOptions[0].textContent);
    for (const def of this.fields) {
      const input = this.control(def.key);
      if (!input || input.disabled) continue;
      const value = def.type === 'checkboxes' ? [...this.form.querySelectorAll(`[name="${def.key}"]:checked`)].map(i => i.value).join(', ') : input.value.trim();
      if (value) row(def.label, value);
    }
    if (this.contact) {
      this.contact.querySelector('[data-contact-body]').value = [...summary.children].map(item => `${item.querySelector('dt').textContent}: ${item.querySelector('dd').textContent}`).join('\n');
    }
    if (this.photoURL) {
      const dd = row('Photo', 'Attached to this local preview only'); const image = document.createElement('img'); image.src = this.photoURL; image.alt = 'Selected equipment photo for this request'; dd.append(image);
    }
  }
  clearPhoto(preserveInput = false) {
    this.photoVersion = (this.photoVersion ?? 0) + 1;
    if (this.photoURL) URL.revokeObjectURL(this.photoURL);
    this.photoURL = null; this.photoBusy = false;
    const input = this.querySelector('[data-photo]'); if (input && !preserveInput) input.value = '';
    const preview = this.querySelector('[data-photo-preview]');
    if (preview) { preview.hidden = true; preview.querySelector('[data-photo-image]').replaceChildren(); }
    const status = this.querySelector('[data-photo-status]'); if (status) status.textContent = '';
  }
  async loadPhoto() {
    const input = this.querySelector('[data-photo]'), file = input.files[0]; this.clearPhoto(true);
    if (!file) return;
    const status = this.querySelector('[data-photo-status]');
    if (!['image/jpeg','image/png','image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) { input.value = ''; status.textContent = 'Choose a JPG, PNG or WebP image no larger than 10 MB.'; return; }
    const version = this.photoVersion, url = URL.createObjectURL(file), image = new Image();
    this.photoBusy = true; status.textContent = 'Checking photo…'; image.src = url;
    try {
      await image.decode();
      if (version !== this.photoVersion) { URL.revokeObjectURL(url); return; }
      if (image.naturalWidth * image.naturalHeight > 40000000) throw new Error('Image dimensions too large');
      this.photoURL = url;
      const preview = this.querySelector('[data-photo-preview]');
      image.alt = 'Your selected equipment photo'; image.width = image.naturalWidth; image.height = image.naturalHeight;
      preview.querySelector('[data-photo-image]').append(image); preview.hidden = false;
      status.textContent = `${file.name} · Local preview only; not uploaded.`;
    } catch {
      URL.revokeObjectURL(url);
      if (version === this.photoVersion) { input.value = ''; status.textContent = 'That image could not be previewed. Try a smaller JPG, PNG or WebP.'; }
    } finally { if (version === this.photoVersion) this.photoBusy = false; }
  }
}
if (!customElements.get('oh-service-request')) customElements.define('oh-service-request', ServiceRequest);
